// Update your WinLossPopUp.js component
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Fade,
  Slide,
  Avatar,
  Grid,
  useTheme,
  useMediaQuery
} from '@mui/material';
import confetti from 'canvas-confetti';
import { CheckCircle, Casino, Celebration, MoodBad } from '@mui/icons-material';
import { money } from '../../utility/Helper1';

const ResultPopup = ({ 
  open, 
  onClose, 
  resultData,
  isWin = false
}) => {
  const theme = useTheme();
  const isMobile = false; //useMediaQuery(theme.breakpoints.down('sm'));
  const [confettiInterval, setConfettiInterval] = React.useState(null);

  // Process game data
  const {
    awarded = isWin ? resultData?.walkAwayAmount || '0.00' : '0.00',
    currency = 'USD',
    transactionId = resultData?.txn_id || 'TX-XXXXXX',
    timestamp = resultData?.timeEarningAwarded || new Date().toLocaleString(),
    numbers = resultData?.winning_number ? [parseInt(resultData.winning_number)] : [0],
    gameName = 'Prediction Game'
  } = resultData || {};

  // Confetti effect (only for wins)
  const fireConfetti = () => {
    if (!isWin) return;

    const end = Date.now() + 3000;
    const colors = ['#ff0000', '#ffff00', '#00ff00', '#00ffff', '#0000ff', '#ff00ff', '#ffffff'];

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: colors
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: colors
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };

    confetti({
      particleCount: 150,
      spread: 100,
      origin: { y: 0.6 }
    });
    frame();
  };

  const stopConfetti = () => {
    if (confettiInterval) {
      clearInterval(confettiInterval);
      setConfettiInterval(null);
    }
  };

  React.useEffect(() => {
    if (open && isWin) {
      fireConfetti();
      const interval = setInterval(() => {
        fireConfetti();
      }, 1500);
      setConfettiInterval(interval);

      return () => {
        stopConfetti();
      };
    } else {
      stopConfetti();
    }
  }, [open, isWin]);

  // Color schemes for win/loss states
  const styles = {
    win: {
      background: 'linear-gradient(135deg, #1a2a6c, #b21f1f, #fdbb2d)',
      borderColor: 'gold',
      textColor: 'gold',
      highlightColor: 'rgba(255, 215, 0, 0.7)',
      iconColor: 'gold',
      amountBackground: 'radial-gradient(circle, gold, #b8860b)',
      buttonGradient: 'linear-gradient(to right, #ffd700, #daa520)'
    },
    loss: {
      background: 'linear-gradient(135deg, #2c3e50, #4a5a6a, #7f8c8d)',
      borderColor: '#aaa',
      textColor: '#ddd',
      highlightColor: 'rgba(170, 170, 170, 0.5)',
      iconColor: '#aaa',
      amountBackground: 'radial-gradient(circle, #aaa, #666)',
      buttonGradient: 'linear-gradient(to right, #aaa, #777)'
    }
  };

  const currentStyle = isWin ? styles.win : styles.loss;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      TransitionComponent={Slide}
      transitionDuration={500}
      PaperProps={{
        sx: {
          background: currentStyle.background,
          borderRadius: 4,
          overflow: 'hidden',
          border: `2px solid ${currentStyle.borderColor}`,
          boxShadow: `0 10px 30px ${currentStyle.highlightColor}`
        }
      }}
    >
      <Fade in={open} timeout={800}>
        <Box>
          <Box
            sx={{
              background: 'rgba(0,0,0,0.7)',
              p: 3,
              textAlign: 'center',
              position: 'relative'
            }}
          >
            {isWin ? (
              <>
                <Celebration
                  sx={{
                    position: 'absolute',
                    top: 16,
                    left: 16,
                    fontSize: 32,
                    color: currentStyle.iconColor
                  }}
                />
                <Celebration
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    fontSize: 32,
                    color: currentStyle.iconColor
                  }}
                />
              </>
            ) : (
              <MoodBad
                sx={{
                  position: 'absolute',
                  top: 16,
                  left: 16,
                  fontSize: 32,
                  color: currentStyle.iconColor
                }}
              />
            )}

            <Typography
              variant="h4"
              component="div"
              sx={{
                color: currentStyle.textColor,
                fontWeight: 'bold',
                mb: 1,
                textShadow: `0 0 10px ${currentStyle.highlightColor}`,
                fontFamily: '"Arial Black", sans-serif'
              }}
            >
              {isWin ? 'CONGRATULATIONS!' : 'YOU LOST!'}
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{ color: 'white', mb: 2 }}
            >
              {isWin ? `You won!` : `Better luck next time`}
            </Typography>

            <Box
              sx={{
                background: 'rgba(0,0,0,0.5)',
                borderRadius: 2,
                p: 2,
                my: 2,
                border: `1px dashed ${currentStyle.borderColor}`
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  color: currentStyle.textColor,
                  fontWeight: 'bold',
                  fontFamily: 'monospace'
                }}
              >
                {money(resultData?.walkAwayAmount, currency)}
              </Typography>
            </Box>

            {isWin && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 1,
                  mb: 2
                }}
              >
                <CheckCircle sx={{ color: 'limegreen', fontSize: 32 }} />
                <Typography
                  variant="body1"
                  sx={{ color: 'white', fontStyle: 'italic' }}
                >
                  Prize successfully credited to your account
                </Typography>
              </Box>
            )}
          </Box>

          <DialogContent
            sx={{
              background: 'rgba(0,0,0,0.8)',
              color: 'white',
              py: 3
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ color: 'lightgray' }}>
                  Transaction ID:
                </Typography>
                <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                  {transactionId}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ color: 'lightgray' }}>
                  Date & Time:
                </Typography>
                <Typography variant="body1">{timestamp}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2, bgcolor: 'rgba(255,255,255,0.1)' }} />
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1
                  }}
                >
                  <Casino sx={{ color: currentStyle.iconColor }} />
                  <Typography variant="h6" sx={{ color: currentStyle.iconColor }}>
                    {isWin ? 'Winning Number' : 'Your Number'}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 2
                  }}
                >
                  {numbers.map((num, index) => (
                    <Avatar
                      key={index}
                      sx={{
                        width: 40,
                        height: 40,
                        fontWeight: 'bold',
                        background: currentStyle.amountBackground,
                        color: 'black'
                      }}
                    >
                      {num}
                    </Avatar>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions
            sx={{
              background: 'rgba(0,0,0,0.9)',
              px: 3,
              py: 2
            }}
          >
            <Button
              onClick={onClose}
              variant="contained"
              fullWidth
              sx={{
                py: 1.5,
                fontWeight: 'bold',
                background: currentStyle.buttonGradient,
                color: 'black',
                '&:hover': {
                  background: currentStyle.buttonGradient
                }
              }}
            >
              {isWin ? 'CLAIM YOUR WINNINGS' : 'TRY AGAIN'}
            </Button>
          </DialogActions>
        </Box>
      </Fade>
    </Dialog>
  );
};

export default ResultPopup;